import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AutoAwesomeOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  LockOpenOutlined,
  LockOutlined,
  PersonOutlined,
  ScheduleSendOutlined,
  VideoLibraryOutlined,
} from "@mui/icons-material";
import PageHeader from "../../common/PageHeader";
import { GlobalContext } from "../../context/GlobalContext";
import ConnectAccountsTab from "./ConnectAccountsTab";
import PickCharacterModal from "./components/PickCharacterModal";
import PickVideoLibraryModal from "./components/PickVideoLibraryModal";
import { PLATFORM_CONNECT } from "./platformConnectConfig";
import { jobModelLabel, truncatePrompt } from "../videoStudio/utils/jobLabels";
import { mediaSrc } from "../../utils/mediaSrc";

// ─── Static definitions ───────────────────────────────────────────────────────

const PLATFORM_DEFS = PLATFORM_CONNECT.map(({ id, label, emoji, color }) => ({
  id,
  label,
  emoji,
  color,
}));

function statusColor(status) {
  if (status === "published") return "success";
  if (status === "failed") return "error";
  if (status === "publishing") return "info";
  if (status === "cancelled") return "default";
  return "warning";
}

function formatDT(dt) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString();
  } catch {
    return String(dt);
  }
}

const POST_CONTENT_TYPES = [
  { id: "video", labelKey: "socialPostTypeVideo", fallback: "Video" },
  { id: "character", labelKey: "socialPostTypeCharacter", fallback: "Character" },
];

// Connect Accounts tab → ./ConnectAccountsTab.js

// ─── Schedule Post tab ────────────────────────────────────────────────────────

function SchedulePostTab({ lang, hitAxios, showSnack, platforms, posts, setPosts }) {
  const [submitting, setSubmitting] = React.useState(false);
  const [generatingCaption, setGeneratingCaption] = React.useState(false);

  const [postType, setPostType] = React.useState("video");
  const [videoUrl, setVideoUrl] = React.useState("");
  const [libraryJob, setLibraryJob] = React.useState(null);
  const [libraryModalOpen, setLibraryModalOpen] = React.useState(false);
  const [selectedCharacter, setSelectedCharacter] = React.useState(null);
  const [characterModalOpen, setCharacterModalOpen] = React.useState(false);
  const [caption, setCaption] = React.useState("");
  const [selectedPlatforms, setSelectedPlatforms] = React.useState([]);
  const [scheduledAt, setScheduledAt] = React.useState(() => {
    const d = new Date();
    d.setHours(d.getHours() + 1);
    d.setMinutes(0, 0, 0);
    return d.toISOString().slice(0, 16);
  });

  function togglePlatform(id) {
    setSelectedPlatforms((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );
  }

  function resolveMediaUrl() {
    if (postType === "character" && selectedCharacter?.photo_url) {
      return mediaSrc(selectedCharacter.photo_url);
    }
    if (libraryJob?.video_url) return libraryJob.video_url;
    if (videoUrl.trim()) return videoUrl.trim();
    return "";
  }

  const hasPickedMedia =
    postType === "character"
      ? Boolean(selectedCharacter?.photo_url)
      : Boolean(libraryJob?.video_url);

  const libraryContextLabel = React.useMemo(() => {
    if (postType === "character" && selectedCharacter) {
      return selectedCharacter.name || lang?.socialPostTypeCharacter || "Character";
    }
    if (!libraryJob) return "";
    if (libraryJob.ui_model === "video-agent") {
      return (
        truncatePrompt(libraryJob.prompt, 80) ||
        lang?.socialSourceVideoAgent ||
        "Video Agent"
      );
    }
    return (
      truncatePrompt(libraryJob.prompt, 80) ||
      jobModelLabel(libraryJob.ui_model, lang) ||
      `Video #${libraryJob.id}`
    );
  }, [libraryJob, selectedCharacter, postType, lang]);

  const scheduleDescription =
    postType === "character"
      ? selectedCharacter?.description || selectedCharacter?.name || ""
      : libraryJob?.prompt || "";

  async function handleGenerateCaption() {
    const url = resolveMediaUrl();
    setGeneratingCaption(true);
    try {
      const res = await hitAxios({
        path: "/api/social-scheduler/generate-caption",
        post: true,
        obj: {
          video_url: url,
          post_type: postType,
          video_title: libraryContextLabel,
          product_name: libraryContextLabel,
          description: scheduleDescription,
        },
        showLoading: false,
      });
      if (res?.data?.success) {
        const d = res.data.data;
        const full = [d.caption, d.hashtags].filter(Boolean).join("\n\n");
        setCaption(full);
        showSnack(lang?.socialCaptionGenerated || "Caption generated!", true);
      } else {
        showSnack(res?.data?.msg || "Caption generation failed", false);
      }
    } finally {
      setGeneratingCaption(false);
    }
  }

  async function handleSchedule() {
    const url = resolveMediaUrl();
    if (!url) {
      showSnack(
        postType === "character"
          ? lang?.socialNeedCharacter ||
              "Choose a character image from your AI Characters list."
          : lang?.socialNeedVideo || "Paste a video URL or choose one from your Video Library.",
        false,
      );
      return;
    }
    if (!caption.trim()) {
      showSnack(lang?.socialNeedCaption || "Write or generate a caption", false);
      return;
    }
    if (!selectedPlatforms.length) {
      showSnack(lang?.socialNeedPlatforms || "Select at least one platform", false);
      return;
    }

    setSubmitting(true);
    try {
      const res = await hitAxios({
        path: "/api/social-scheduler",
        post: true,
        obj: {
          video_url: url,
          post_type: postType,
          caption: caption.trim(),
          platforms: selectedPlatforms,
          scheduled_at: new Date(scheduledAt).toISOString(),
          product_name: libraryContextLabel,
          description: scheduleDescription,
          video_studio_job_id:
            postType !== "character" &&
            libraryJob &&
            !String(libraryJob.id).startsWith("agent-")
              ? Number(libraryJob.id)
              : undefined,
          influencer_id:
            postType === "character" && selectedCharacter?.id
              ? Number(selectedCharacter.id)
              : undefined,
        },
      });
      if (res?.data?.success) {
        showSnack(lang?.socialScheduled || "Post scheduled!", true);
        const newPost = res.data.data?.post;
        if (newPost) setPosts((prev) => [newPost, ...prev]);
        setVideoUrl("");
        setLibraryJob(null);
        setSelectedCharacter(null);
        setCaption("");
        setSelectedPlatforms([]);
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCancel(id) {
    const res = await hitAxios({
      path: `/api/social-scheduler/${id}/cancel`,
      post: true,
      obj: {},
    });
    if (res?.data?.success) {
      showSnack(lang?.socialCancelled || "Post cancelled", true);
      setPosts((prev) =>
        prev.map((p) => (p.id === id ? { ...p, status: "cancelled" } : p)),
      );
    }
  }

  return (
    <Box>
      {/* Schedule form */}
      <Card
        variant="outlined"
        data-tour="scheduler-form"
        sx={{ borderRadius: 2, mb: 3 }}
      >
        <CardContent>
          <Stack spacing={2.5}>
            <Typography variant="subtitle1" fontWeight={700}>
              {lang?.socialNewPost || "Schedule a post"}
            </Typography>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {lang?.socialPostType || "Post content type"}
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={postType}
                onChange={(_, v) => {
                  if (!v) return;
                  setPostType(v);
                  setLibraryJob(null);
                  setSelectedCharacter(null);
                }}
                sx={{ flexWrap: "wrap" }}
              >
                {POST_CONTENT_TYPES.map((t) => (
                  <ToggleButton key={t.id} value={t.id} sx={{ textTransform: "none", fontWeight: 700 }}>
                    {lang?.[t.labelKey] || t.fallback}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              {postType === "character" ? (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  {lang?.socialCharacterTabPrompt ||
                    "Choose a character image from your AI Characters (influencers) list."}
                </Alert>
              ) : (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  {lang?.socialVideoTabPrompt ||
                    "Any completed clip from Video Studio, uploads, stitches, or Video Agent."}
                </Typography>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {postType === "character"
                  ? lang?.socialCharacterImageSource || "Character image"
                  : lang?.socialVideoSource || "Video"}
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                <Button
                  variant="outlined"
                  startIcon={postType === "character" ? <PersonOutlined /> : <VideoLibraryOutlined />}
                  onClick={() =>
                    postType === "character"
                      ? setCharacterModalOpen(true)
                      : setLibraryModalOpen(true)
                  }
                  sx={{ fontWeight: 700, flexShrink: 0 }}
                >
                  {postType === "character"
                    ? lang?.socialPickCharacter || "Choose character"
                    : lang?.socialPickLibrary || "Choose from Video Library"}
                </Button>
                {hasPickedMedia ? (
                  <Chip
                    label={libraryContextLabel}
                    onDelete={() => {
                      if (postType === "character") setSelectedCharacter(null);
                      else setLibraryJob(null);
                    }}
                    sx={{ maxWidth: { xs: "100%", sm: 360 } }}
                  />
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    {postType === "character"
                      ? lang?.socialPickLibraryHintCharacter ||
                        "Pick a character you already created on the Characters page."
                      : lang?.socialPickLibraryHint ||
                        "Pick any completed Video Studio, upload, stitch, or Video Agent clip."}
                  </Typography>
                )}
              </Stack>
              {hasPickedMedia ? (
                postType === "character" ? (
                  <Box
                    component="img"
                    src={mediaSrc(selectedCharacter.photo_url)}
                    alt={selectedCharacter.name || ""}
                    sx={{
                      mt: 1.5,
                      width: "100%",
                      maxHeight: 280,
                      objectFit: "contain",
                      borderRadius: 2,
                      bgcolor: "grey.100",
                    }}
                  />
                ) : (
                  <Box
                    component="video"
                    src={libraryJob.video_url}
                    controls
                    playsInline
                    sx={{ mt: 1.5, width: "100%", maxHeight: 220, borderRadius: 2, bgcolor: "#000" }}
                  />
                )
              ) : null}
            </Box>

            {postType === "video" ? (
              <TextField
                label={lang?.socialVideoUrl || "Or paste video URL"}
                fullWidth
                size="small"
                value={videoUrl}
                onChange={(e) => {
                  setVideoUrl(e.target.value);
                  if (e.target.value.trim()) setLibraryJob(null);
                }}
                placeholder="https://…"
                disabled={hasPickedMedia}
                helperText={
                  hasPickedMedia
                    ? lang?.socialVideoUrlDisabled || "Clear library selection to paste a URL instead."
                    : ""
                }
              />
            ) : null}

            <PickVideoLibraryModal
              open={libraryModalOpen}
              onClose={() => setLibraryModalOpen(false)}
              lang={lang}
              selectedJob={libraryJob}
              onConfirm={(job) => {
                setLibraryJob(job);
                setVideoUrl("");
              }}
            />

            <PickCharacterModal
              open={characterModalOpen}
              onClose={() => setCharacterModalOpen(false)}
              lang={lang}
              selectedCharacter={selectedCharacter}
              onConfirm={(character) => {
                setSelectedCharacter(character);
                setVideoUrl("");
              }}
            />

            {/* Caption */}
            <Box data-tour="scheduler-caption">
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 0.5 }}
              >
                <Typography variant="subtitle2">
                  {lang?.socialCaption || "Caption"}
                </Typography>
                <Button
                  size="small"
                  startIcon={
                    generatingCaption ? (
                      <CircularProgress size={14} />
                    ) : (
                      <AutoAwesomeOutlined />
                    )
                  }
                  onClick={handleGenerateCaption}
                  disabled={generatingCaption}
                >
                  {lang?.socialGenerateCaption || "AI Generate"}
                </Button>
              </Stack>
              <TextField
                multiline
                rows={4}
                fullWidth
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder={
                  postType === "character"
                    ? lang?.socialCaptionPlaceholderCharacter ||
                      "Write a creator-style caption and hashtags, or click AI Generate…"
                    : lang?.socialCaptionPlaceholder ||
                      "Write your caption and hashtags here, or click AI Generate…"
                }
              />
            </Box>

            {/* Platform chips */}
            <Box data-tour="scheduler-platforms">
              <Typography variant="subtitle2" gutterBottom>
                {lang?.socialPlatforms || "Platforms"}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {PLATFORM_DEFS.map((p) => {
                  const plat = platforms.find((pl) => pl.id === p.id);
                  const isConfigured = !!plat?.configured;
                  const selected = selectedPlatforms.includes(p.id);
                  return (
                    <Tooltip
                      key={p.id}
                      title={
                        !isConfigured
                          ? lang?.socialPlatformLocked?.replace("{platform}", p.label) ||
                            `Link ${p.label} in the Link Accounts tab first`
                          : ""
                      }
                    >
                      <span>
                        <Chip
                          label={
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <span>{p.emoji}</span>
                              <span>{p.label}</span>
                              {!isConfigured && <LockOutlined sx={{ fontSize: 12 }} />}
                              {isConfigured && (
                                <CheckCircleOutlined
                                  sx={{ fontSize: 12, color: selected ? "inherit" : "success.main" }}
                                />
                              )}
                            </Stack>
                          }
                          size="medium"
                          clickable={isConfigured}
                          onClick={isConfigured ? () => togglePlatform(p.id) : undefined}
                          color={selected && isConfigured ? "primary" : "default"}
                          variant={selected && isConfigured ? "filled" : "outlined"}
                          sx={!isConfigured ? { opacity: 0.5 } : undefined}
                        />
                      </span>
                    </Tooltip>
                  );
                })}
              </Stack>
              {platforms.every((p) => !p.configured) && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                  No platforms connected yet — go to the <strong>Connect Accounts</strong> tab to add your credentials.
                </Typography>
              )}
            </Box>

            {/* Datetime picker */}
            <TextField
              label={lang?.socialScheduleAt || "Schedule date & time"}
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ maxWidth: 300 }}
            />

            <Button
              variant="contained"
              data-tour="scheduler-submit"
              startIcon={
                submitting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <ScheduleSendOutlined />
                )
              }
              onClick={handleSchedule}
              disabled={submitting}
            >
              {lang?.socialScheduleBtn || "Schedule post"}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Scheduled posts table */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        {lang?.socialHistoryTitle || "Scheduled posts"}
      </Typography>

      {posts.length === 0 ? (
        <Alert severity="info">
          {lang?.socialEmpty || "No scheduled posts yet. Set one up above."}
        </Alert>
      ) : (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Video</TableCell>
                <TableCell>Caption</TableCell>
                <TableCell>Platforms</TableCell>
                <TableCell>Scheduled</TableCell>
                <TableCell>Status</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {posts.map((post) => (
                <TableRow key={post.id}>
                  <TableCell>
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{ maxWidth: 120, display: "block" }}
                      title={post.video_url}
                    >
                      {post.video_url?.split("/").pop()?.slice(0, 20) || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{ maxWidth: 180, display: "block" }}
                      title={post.caption}
                    >
                      {post.caption?.slice(0, 60)}
                      {(post.caption?.length || 0) > 60 ? "…" : ""}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" flexWrap="wrap" gap={0.4}>
                      {(post.platforms || []).map((p) => {
                        const def = PLATFORM_DEFS.find((d) => d.id === p);
                        return (
                          <Chip
                            key={p}
                            size="small"
                            label={def ? `${def.emoji} ${def.label}` : p}
                            variant="outlined"
                          />
                        );
                      })}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{formatDT(post.scheduled_at)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={post.status}
                      color={statusColor(post.status)}
                    />
                  </TableCell>
                  <TableCell>
                    {(post.status === "scheduled" || post.status === "publishing") && (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleCancel(post.id)}
                      >
                        {lang?.cancel || "Cancel"}
                      </Button>
                    )}
                    {post.status === "failed" && post.platform_results && (
                      <Tooltip
                        title={Object.entries(post.platform_results)
                          .map(([p, r]) => `${p}: ${r?.msg || JSON.stringify(r)}`)
                          .join("\n")}
                        arrow
                      >
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ cursor: "help", textDecoration: "underline dotted" }}
                        >
                          Details
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </Box>
  );
}

// ─── Root component ───────────────────────────────────────────────────────────

const SocialScheduler = ({ lang }) => {
  const { hitAxios, showSnack } = React.useContext(GlobalContext);

  const [tab, setTab] = React.useState(0);
  const [platforms, setPlatforms] = React.useState([]);
  const [posts, setPosts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const onPrepare = (e) => {
      if (e.detail?.tourId === "scheduler") setTab(0);
    };
    window.addEventListener("beyundai:prepare-tour", onPrepare);
    return () => window.removeEventListener("beyundai:prepare-tour", onPrepare);
  }, []);

  const loadData = React.useCallback(async () => {
    setLoading(true);
    try {
      const [platRes, postsRes] = await Promise.all([
        hitAxios({ path: "/api/social-scheduler/platforms", post: false, showLoading: false }),
        hitAxios({ path: "/api/social-scheduler", post: false, showLoading: false }),
      ]);

      if (platRes?.data?.success) {
        setPlatforms(
          Array.isArray(platRes.data.data?.platforms) ? platRes.data.data.platforms : [],
        );
      }
      if (postsRes?.data?.success) {
        setPosts(
          Array.isArray(postsRes.data.data?.posts) ? postsRes.data.data.posts : [],
        );
      }
    } finally {
      setLoading(false);
    }
  }, [hitAxios]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-poll posts that are pending/publishing
  React.useEffect(() => {
    const active = posts.some(
      (p) => p.status === "scheduled" || p.status === "publishing",
    );
    if (!active) return;

    const timer = setTimeout(async () => {
      const res = await hitAxios({
        path: "/api/social-scheduler",
        post: false,
        showLoading: false,
      });
      if (res?.data?.success) {
        setPosts(Array.isArray(res.data.data?.posts) ? res.data.data.posts : []);
      }
    }, 4000);

    return () => clearTimeout(timer);
  }, [posts, hitAxios]);

  // Refresh platform status when switching back to Schedule tab from Connect tab
  function handleTabChange(_, newTab) {
    setTab(newTab);
    if (newTab === 0) {
      // Refresh platform connection status
      hitAxios({ path: "/api/social-scheduler/platforms", post: false, showLoading: false }).then(
        (res) => {
          if (res?.data?.success) {
            setPlatforms(
              Array.isArray(res.data.data?.platforms) ? res.data.data.platforms : [],
            );
          }
        },
      );
    }
  }

  const connectedCount = platforms.filter((p) => p.configured).length;

  if (loading) return <CircularProgress size={28} />;

  return (
    <Box>
      <PageHeader
        title={lang?.socialTitle || "Social Scheduler"}
        subtitle={
          lang?.socialSubtitle ||
          "Schedule videos and character clips to TikTok, Instagram, YouTube, and Facebook."
        }
        icon={ScheduleSendOutlined}
      />

      <Tabs
        value={tab}
        onChange={handleTabChange}
        data-tour="scheduler-tabs"
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab
          data-tour="scheduler-tab-schedule"
          label={
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <ScheduleSendOutlined fontSize="small" />
              <span>{lang?.socialTabSchedule || "Schedule Post"}</span>
            </Stack>
          }
        />
        <Tab
          data-tour="scheduler-tab-connect"
          label={
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <LockOpenOutlined fontSize="small" />
              <span>{lang?.socialTabConnect || "Link Accounts"}</span>
              {connectedCount > 0 && (
                <Chip
                  size="small"
                  label={`${connectedCount}/4`}
                  color="success"
                  sx={{ height: 18, fontSize: 11 }}
                />
              )}
            </Stack>
          }
        />
      </Tabs>

      {tab === 0 && (
        <SchedulePostTab
          lang={lang}
          hitAxios={hitAxios}
          showSnack={showSnack}
          platforms={platforms}
          posts={posts}
          setPosts={setPosts}
        />
      )}

      {tab === 1 && (
        <ConnectAccountsTab hitAxios={hitAxios} showSnack={showSnack} />
      )}
    </Box>
  );
};

export default SocialScheduler;
